import type { AppLanguage } from "@/constants/i18n";

export const AUTH_QUERY_PARAMS = {
  next: "next",
  registered: "registered",
} as const;

export const AUTH_QUERY_VALUES = {
  true: "1",
} as const;

export const ROUTES = {
  home: "/",
  pricing: "/pricing",
  legal: "/legal",
  legalTerms: "/legal/terms",
  legalPrivacy: "/legal/privacy",
  legalWorkspacePrivacy: "/legal/workspace-privacy",
  legalCompanyDetails: "/legal/company-details",
  legalSubscription: "/legal/subscription",
  legalPersonalDataConsent: "/legal/personal-data-consent",
  account: "/account",
  accountComponent: "/account/components/:componentId",
  profile: "/account/profile",
  realmDomainLogin: "/account/realms/:realmUuid/domain-login",
  projectDetail: "/account/projects/:projectUuid",
  organizationDetail: "/account/organizations/:organizationUuid",
  elementStore: "/account/element-store",
  users: "/account/users",
  userDetail: "/account/users/:userUuid",
  login: "/login",
  loginAfterRegistration: `/login?${AUTH_QUERY_PARAMS.registered}=${AUTH_QUERY_VALUES.true}`,
  register: "/register",
  confirmEmail: "/confirm-email",
  forgotPassword: "/forgot-password",
  resetPassword: "/reset-password",
} as const;

export const EXTERNAL_LINKS = {
  website: "https://exordos.com",
  company: "https://genesis-corporation.ru",
  docs: {
    en: "https://docs.exordos.com/",
    ru: "https://docs.exordos.com/index.ru",
  },
  github: "https://github.com/exordos",
  installScript: "https://repo.exordos.com/install.sh",
} as const;

export type LocalizedHref = string | Readonly<Record<AppLanguage, string>>;

export function resolveHref(href: LocalizedHref, language: AppLanguage): string {
  return typeof href === "string" ? href : href[language];
}

export const INSTALL_COMMAND = `curl -fsSL ${EXTERNAL_LINKS.installScript} | sh`;

export function getRealmApiUrl(domain: string, path: string): string {
  const protocol = window.location.protocol;
  return `${protocol}//${domain}${path}`;
}

export function hasRegistrationNotice(searchParams: URLSearchParams): boolean {
  return searchParams.get(AUTH_QUERY_PARAMS.registered) === AUTH_QUERY_VALUES.true;
}

// The billing page is drawn by the interface builder. Its manifest
// (exordos/manifests/ecosystem_ui_billing.yaml.j2) pins this uuid, which is also
// where the payment provider sends a visitor back to.
export const BILLING_PAGE_COMPONENT_ID = "b1111111-b111-4111-8111-b11111111111";
export const TARIFFS_PAGE_COMPONENT_ID = "b2222222-b222-4222-8222-b22222222222";

export function getAccountComponentRoute(componentId: string): string {
  return `${ROUTES.account}/components/${encodeURIComponent(componentId)}`;
}

export function getUserDetailRoute(userUuid: string): string {
  return `${ROUTES.account}/users/${encodeURIComponent(userUuid)}`;
}

export function isAccountRoutePath(pathname: string): boolean {
  return pathname === ROUTES.account || pathname.startsWith(`${ROUTES.account}/`);
}

export function getLoginRouteWithNext(nextRoute: string): string {
  const safeNextRoute = getSafePostLoginRoute(nextRoute);
  if (!safeNextRoute) return ROUTES.login;

  return `${ROUTES.login}?${AUTH_QUERY_PARAMS.next}=${encodeURIComponent(safeNextRoute)}`;
}

export function getPostLoginRedirect(searchParams: URLSearchParams): string {
  return getSafePostLoginRoute(searchParams.get(AUTH_QUERY_PARAMS.next))
    ?? ROUTES.account;
}

function getSafePostLoginRoute(value: string | null): string | null {
  if (!value) return null;
  if (!value.startsWith("/") || value.startsWith("//")) return null;

  const pathname = value.split(/[?#]/)[0];
  if (!isPostLoginRoutePath(pathname)) return null;

  return value;
}

function isPostLoginRoutePath(pathname: string): boolean {
  return pathname === ROUTES.home || isAccountRoutePath(pathname);
}

export type HeaderNavItem =
  | {
      id: "pricing" | "elements";
      labelKey: "nav.pricing" | "nav.elements";
      type: "internal";
      to: string;
      end?: boolean;
    }
  | {
      id: "docs" | "github";
      labelKey: "nav.docs" | "nav.code";
      type: "external";
      href: LocalizedHref;
      withGithubIcon?: boolean;
    };

export const HEADER_NAV_ITEMS: readonly HeaderNavItem[] = [
  {
    id: "pricing",
    labelKey: "nav.pricing",
    type: "internal",
    to: `${ROUTES.home}#pricing`,
    end: true,
  },
  {
    id: "elements",
    labelKey: "nav.elements",
    type: "internal",
    to: ROUTES.elementStore,
  },
  {
    id: "docs",
    labelKey: "nav.docs",
    type: "external",
    href: EXTERNAL_LINKS.docs,
  },
  {
    id: "github",
    labelKey: "nav.code",
    type: "external",
    href: EXTERNAL_LINKS.github,
  },
];
